import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { iniciarNovaConversaAssistenteIA, responderAssistenteIA } from "@/api/space/control/assistente-ia.spaceApi";
import { obterErroApi } from "@/utils/errors/apiError";
import { generateRandomId } from "@/utils/validators/helpers";
import { AppAssistenteIAContext, type AppAssistenteIAContextType } from "./AppAssistenteIAContext";
import type { AssistenteIAContextoTela, AssistenteIAMensagem, AssistenteIAMensagemStatus } from "../types";

// ------------------------------- CONSTANTS -------------------------------
const MENSAGEM_ERRO_PADRAO = "Não foi possível responder agora. Tente novamente.";
const MENSAGEM_ERRO_LIMPEZA = "Não foi possível limpar a conversa. Tente novamente.";

// --------------------------------- TYPES ---------------------------------
type AppAssistenteIAProviderProps = {
	readonly children: ReactNode;
};

// -------------------------------- HELPERS --------------------------------
function criarMensagem(
	papel: AssistenteIAMensagem["papel"],
	conteudo: string,
	status: AssistenteIAMensagemStatus,
): AssistenteIAMensagem {
	return {
		id: generateRandomId(),
		papel,
		conteudo,
		criadoEm: new Date().toISOString(),
		status,
	};
}

// ------------------------------- COMPONENT -------------------------------
export function AppAssistenteIAProvider({ children }: Readonly<AppAssistenteIAProviderProps>) {
	// -------------------------------- STATES --------------------------------
	const contextoRef = useRef<AssistenteIAContextoTela | null>(null);
	const operacaoRef = useRef<AbortController | null>(null);

	const [contexto, setContexto] = useState<AssistenteIAContextoTela | null>(null);
	const [mensagens, setMensagens] = useState<AssistenteIAMensagem[]>([]);
	const [aberto, setAberto] = useState(false);
	const [expandido, setExpandido] = useState(false);
	const [operacao, setOperacao] = useState<"envio" | "limpeza" | null>(null);
	const [erro, setErro] = useState<string | null>(null);
	const [manterContexto, setManterContexto] = useState(true);

	// Aborta o envio e invalida também a conclusão da limpeza, que não recebe signal na API atual.
	const cancelarOperacaoPendente = useCallback(() => {
		operacaoRef.current?.abort();
		operacaoRef.current = null;
		setOperacao(null);
	}, []);

	// -------------------------------- EFFECTS -------------------------------
	useEffect(() => cancelarOperacaoPendente, [cancelarOperacaoPendente]);

	// ----------------------------- DERIVED STATE ----------------------------
	const enviando = operacao === "envio";
	const limpando = operacao === "limpeza";

	// ------------------------------- HANDLERS -------------------------------
	const atualizarStatusMensagem = useCallback((id: string, status: AssistenteIAMensagemStatus) => {
		setMensagens((mensagensAtuais) =>
			mensagensAtuais.map((mensagem) => (mensagem.id === id ? { ...mensagem, status } : mensagem)),
		);
	}, []);

	const registrarContexto = useCallback(
		(novoContexto: AssistenteIAContextoTela) => {
			const contextoAnterior = contextoRef.current;
			const mudouContexto =
				contextoAnterior?.id !== novoContexto.id ||
				contextoAnterior?.tela !== novoContexto.tela ||
				contextoAnterior?.chave !== novoContexto.chave;

			contextoRef.current = novoContexto;
			setContexto(novoContexto);

			if (mudouContexto) {
				cancelarOperacaoPendente();
				setErro(null);
				setMensagens([]);
			}
		},
		[cancelarOperacaoPendente],
	);

	const limparContexto = useCallback(
		(id: string) => {
			if (contextoRef.current?.id !== id) return;

			cancelarOperacaoPendente();
			contextoRef.current = null;
			setContexto(null);
			setMensagens([]);
			setErro(null);
			setAberto(false);
			setExpandido(false);
		},
		[cancelarOperacaoPendente],
	);

	const abrir = useCallback(() => {
		if (!contextoRef.current) return;
		setAberto(true);
	}, []);

	const fechar = useCallback(() => {
		setAberto(false);
	}, []);

	const alternarExpandido = useCallback(() => {
		setExpandido((valorAtual) => !valorAtual);
	}, []);

	const definirManterContexto = useCallback((valor: boolean) => {
		if (operacaoRef.current !== null) return;
		setManterContexto(valor);
	}, []);

	const executarEnvioMensagem = useCallback(
		async (conteudo: string) => {
			const pergunta = conteudo.trim();
			const contextoAtual = contextoRef.current;
			if (!pergunta || !contextoAtual || operacaoRef.current !== null) return;

			const mensagemUsuario = criarMensagem("usuario", pergunta, "pendente");
			const controller = new AbortController();
			operacaoRef.current = controller;

			setErro(null);
			setOperacao("envio");
			setMensagens((mensagensAtuais) => [...mensagensAtuais, mensagemUsuario]);

			try {
				const resposta = await responderAssistenteIA(
					{
						mensagem: pergunta,
						tela: contextoAtual.tela,
						contexto: contextoAtual.contexto ?? null,
						manter_contexto: manterContexto,
					},
					controller.signal,
				);
				if (operacaoRef.current !== controller) return;

				const mensagemAssistente = criarMensagem("assistente", resposta.mensagem, "enviada");
				atualizarStatusMensagem(mensagemUsuario.id, "enviada");
				setMensagens((mensagensAtuais) => [...mensagensAtuais, mensagemAssistente]);
			} catch (error) {
				if (operacaoRef.current !== controller) return;

				atualizarStatusMensagem(mensagemUsuario.id, "erro");
				setErro(obterErroApi(error)?.message ?? MENSAGEM_ERRO_PADRAO);
			} finally {
				if (operacaoRef.current === controller) {
					operacaoRef.current = null;
					setOperacao(null);
				}
			}
		},
		[atualizarStatusMensagem, manterContexto],
	);

	const enviarMensagem = useCallback(
		(conteudo: string) => {
			void executarEnvioMensagem(conteudo);
		},
		[executarEnvioMensagem],
	);

	const executarLimpezaConversa = useCallback(async () => {
		const contextoAtual = contextoRef.current;
		if (!contextoAtual || operacaoRef.current !== null) return;

		const controller = new AbortController();
		operacaoRef.current = controller;
		setOperacao("limpeza");
		setErro(null);

		try {
			const resposta = await iniciarNovaConversaAssistenteIA(contextoAtual.tela);
			if (operacaoRef.current !== controller) return;
			if (resposta.sucesso !== true) {
				setErro(MENSAGEM_ERRO_LIMPEZA);
				return;
			}
			setMensagens([]);
		} catch (error) {
			if (operacaoRef.current !== controller) return;
			setErro(obterErroApi(error)?.message ?? MENSAGEM_ERRO_LIMPEZA);
		} finally {
			if (operacaoRef.current === controller) {
				operacaoRef.current = null;
				setOperacao(null);
			}
		}
	}, []);

	const limparConversa = useCallback(() => {
		void executarLimpezaConversa();
	}, [executarLimpezaConversa]);

	// -------------------------------- PROPS ---------------------------------
	const value = useMemo<AppAssistenteIAContextType>(
		() => ({
			contexto,
			mensagens,
			aberto,
			expandido,
			enviando,
			limpando,
			erro,
			manterContexto,
			registrarContexto,
			limparContexto,
			abrir,
			fechar,
			alternarExpandido,
			definirManterContexto,
			enviarMensagem,
			limparConversa,
		}),
		[
			aberto,
			alternarExpandido,
			abrir,
			contexto,
			definirManterContexto,
			enviando,
			enviarMensagem,
			erro,
			fechar,
			limparContexto,
			limparConversa,
			limpando,
			manterContexto,
			mensagens,
			registrarContexto,
			expandido,
		],
	);

	// -------------------------------- RETURN --------------------------------
	return <AppAssistenteIAContext value={value}>{children}</AppAssistenteIAContext>;
}
